import React from 'react';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import { useTicketGroups } from '../TicketGroupsContext';
import Button from '@dashboard/components/Button';
import Reorderable from '@dashboard/components/Reorderable';
import TicketTypeEdit from './TicketTypeEdit';
import TicketGroupListItem from './TicketGroupListItem';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import clsx from 'clsx';
import { Label } from '@dashboard/components/Form';
import checkIsTicketGroupSoldOut from '../checkIsTicketGroupSoldOut';
import Callout from '@dashboard/components/Callout';
import TicketTypesList from './TicketTypesList';
import TicketGroupLayoutForm from './TicketGroupLayoutForm';
import TicketGroupSettingsForm from './TicketGroupSettingsForm';
import { Cog8ToothIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import Tooltip from '@dashboard/components/Tooltip';
import { confirm } from '@dashboard/lib/confirm';

export default function TicketGroupsList() {
  const { state: ticketGroups } = useTicketGroups();
  const {
    onReorderTicketGroups,
    isAddingTicketType,
    isEditingTicketType,
    onAddTicketGroup,
    editingTicketTypeId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isEditingTicketGroupLayout,
    setIsEditingTicketGroupLayout,
    isEditingTicketGroupSettings,
    setIsEditingTicketGroupSettings,
  } = useTicketGroupEdit();

  const { onAddTicketType } = useTicketTypeHelpers();

  /* const [ticketGroupsSettingsOpen, setTicketGroupsSettingsOpen] =
    useState(false); */

  const enabledTicketGroupsCount = ticketGroups.filter(
    ticketGroup => ticketGroup.status === 'enabled',
  ).length;

  if (ticketGroups.length === 0) {
    return (
      <div className="flex">
        <Button handleClick={onAddTicketGroup} appearance="primary">
          + Add ticket
        </Button>
      </div>
    );
  }

  if (enabledTicketGroupsCount === 1 && ticketGroups.length === 1) {
    const ticketGroup = ticketGroups[0];

    const isTicketGroupSoldOut = checkIsTicketGroupSoldOut(ticketGroup);

    if (ticketGroup.ticketTypes.length === 0) {
      return (
        <div>
          {!isAddingTicketType && (
            <Button handleClick={onAddTicketType} appearance="primary">
              + Add ticket type
            </Button>
          )}

          {isAddingTicketType && (
            <div
              className={clsx('mt-4 pr-8', {
                'pl-6': ticketGroup.ticketTypes.length > 0,
              })}
            >
              <Label>New ticket type</Label>

              <TicketTypeEdit ticketGroupId={ticketGroup._id} />
            </div>
          )}
        </div>
      );
    }

    const isEditingLastTicketType =
      isEditingTicketType &&
      editingTicketTypeId === ticketGroup.ticketTypes[ticketGroup.ticketTypes.length - 1]._id;

    return (
      <>
        <div className="mb-20">
          {isTicketGroupSoldOut && (
            <Callout variant="info">
              <Callout.Heading>Ticket group sold out</Callout.Heading>
            </Callout>
          )}

          <div className="flex flex-col gap-y-2">
            <TicketTypesList
              ticketTypes={ticketGroup.ticketTypes}
              ticketGroupId={ticketGroup._id}
            />
          </div>

          {isAddingTicketType && (
            <div
              className={clsx('mt-4 pr-8', {
                'pl-6': ticketGroup.ticketTypes.length > 0,
              })}
            >
              <Label>New ticket type</Label>
              <TicketTypeEdit ticketGroupId={ticketGroup._id} />
            </div>
          )}

          {!isAddingTicketType && !isEditingTicketType && (
            <>
              <div
                className={clsx('mt-4 flex gap-x-2 ml-6 mr-[26px] justify-between', {
                  'border-t pt-8': isEditingLastTicketType,
                })}
              >
                <div className="flex gap-x-2">
                  <Tooltip label="Settings">
                    <Button
                      handleClick={async () => {
                        let proceed = true;

                        if (hasUnsavedChanges) {
                          proceed = await confirm('Discard unsaved changes?', {
                            confirm: 'Discard',
                          });
                        }

                        if (!proceed) return;

                        setIsEditingTicketGroupSettings(!isEditingTicketGroupSettings);
                        setIsEditingTicketGroupLayout(false);
                        setHasUnsavedChanges(false);
                      }}
                      appearance="secondary"
                      className={clsx({
                        '!border-chocolate-1000': isEditingTicketGroupSettings,
                      })}
                    >
                      <Cog8ToothIcon className="w-5 h-5" />
                    </Button>
                  </Tooltip>

                  <Tooltip label="Layout">
                    <Button
                      handleClick={async () => {
                        let proceed = true;

                        if (hasUnsavedChanges) {
                          proceed = await confirm('Discard unsaved changes?', {
                            confirm: 'Discard',
                          });
                        }

                        if (!proceed) return;

                        setIsEditingTicketGroupLayout(!isEditingTicketGroupLayout);
                        setIsEditingTicketGroupSettings(false);
                        setHasUnsavedChanges(false);
                      }}
                      appearance="secondary"
                      className={clsx({
                        '!border-chocolate-1000': isEditingTicketGroupLayout,
                      })}
                    >
                      <Squares2X2Icon className="w-5 h-5" />
                    </Button>
                  </Tooltip>

                  <Button handleClick={onAddTicketGroup} appearance="secondary">
                    + Add group
                  </Button>
                </div>

                <Button appearance="primary" onClick={() => onAddTicketType(ticketGroup._id)}>
                  + Add ticket type
                </Button>
              </div>

              <div className="ml-6 mr-[26px] mt-4">
                {isEditingTicketGroupLayout && (
                  <TicketGroupLayoutForm
                    ticketGroup={ticketGroup}
                    onClose={() => {
                      setIsEditingTicketGroupLayout(false);
                      setHasUnsavedChanges(false);
                    }}
                  />
                )}

                {isEditingTicketGroupSettings && (
                  <TicketGroupSettingsForm
                    ticketGroup={ticketGroup}
                    onClose={() => {
                      setIsEditingTicketGroupSettings(false);
                      setHasUnsavedChanges(false);
                    }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Reorderable.Wrapper>
          <Reorderable
            id="ticketGroups"
            items={ticketGroups}
            onReorder={onReorderTicketGroups}
            renderItem={({ item: ticketGroup, dragHandleProps, isDragging }) => (
              <Reorderable.Item isDragging={isDragging}>
                <TicketGroupListItem ticketGroup={ticketGroup} dragHandleProps={dragHandleProps} />
              </Reorderable.Item>
            )}
          />
        </Reorderable.Wrapper>
      </div>

      {!isAddingTicketType && !isEditingTicketGroupLayout && !isEditingTicketGroupSettings && (
        <div className="mt-8">
          {/*           <div className="mb-8">
            <Collapsible.Root
              open={ticketGroupsSettingsOpen}
              onOpenChange={setTicketGroupsSettingsOpen}
            >
              <Collapsible.Trigger className="flex items-center gap-x-1 text-gray-900 hover:text-gray-500 font-medium text-sm">
                <div className="flex-1 text-left">Settings & layout</div>

                {ticketGroupsSettingsOpen ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </Collapsible.Trigger>

              <Collapsible.Content className="CollapsibleContentAnimation mt-2">
                <TicketGroupsSettings />
              </Collapsible.Content>
            </Collapsible.Root>
          </div> */}

          <Button handleClick={onAddTicketGroup} appearance="secondary">
            + Add group
          </Button>
        </div>
      )}
    </div>
  );
}
